import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { ROUTES } from '@/routes.ts';
import {
  NEW_CONSULT_PRICE_CENTS,
  PLATFORM_FEE_CENTS,
  PRESCRIPTION_CONSULT_PRICE_CENTS,
  splitVatInclusive,
} from '@dermalink/shared';
import type {
  InvoicePdfUrlResponse,
  OrgAdminInvoiceListResponse,
  OrgAdminMollieConnectResponse,
  OrgAdminMollieStatusResponse,
  OrgAdminOrganization,
} from '@dermalink/shared';
import { PracticeAdminHead } from './PracticeAdmin.tsx';
import { usePracticeAdmin } from './practiceAdminContext.ts';
import settings from './PracticeSettings.module.css';
import admin from './PracticeAdmin.module.css';
import styles from './PracticePayments.module.css';

const euro = (cents: number) => `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;

const mollieQueryKey = (orgId: number) => ['org-admin-mollie', orgId];

export function PracticePayments() {
  const { org } = usePracticeAdmin();
  return (
    <div className={admin.container}>
      <div className={`${admin.inner} ${admin.innerNarrow}`}>
        <PracticeAdminHead title="Betalingen" />
        <MollieCard key={org.id} org={org} />
        <FeeCard />
        <InvoicesCard key={`invoices-${org.id}`} org={org} />
      </div>
    </div>
  );
}

function MollieCard({ org }: { org: OrgAdminOrganization }) {
  const justLinked  = (useLocation().state as { mollieLinked?: boolean } | null)?.mollieLinked === true;
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const [busy, setBusy]   = useState<'connect' | 'disconnect' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<OrgAdminMollieStatusResponse>({
    queryKey: mollieQueryKey(org.id),
    queryFn:  async () => {
      const res = await authFetch(`/org-admin/organizations/${org.id}/mollie`);
      if (!res.ok) throw new Error('Failed to load Mollie status');
      return res.json();
    },
  });

  async function handleConnect() {
    setBusy('connect');
    setError(null);
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/mollie/connect`, { method: 'POST' });
      if (!res.ok) throw new Error('Kon de koppeling met Mollie niet starten.');
      const { url } = await res.json() as OrgAdminMollieConnectResponse;
      window.location.assign(url);
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  async function handleDisconnect() {
    setBusy('disconnect');
    setError(null);
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/mollie/disconnect`, { method: 'POST' });
      const body = await res.json() as OrgAdminMollieStatusResponse & { message?: string };
      if (!res.ok) throw new Error(body.message ?? 'Ontkoppelen mislukt.');
      queryClient.setQueryData(mollieQueryKey(org.id), body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={settings.card}>
      <p className={settings.sectionLabel}>Mollie-account</p>
      {isLoading && <p className={settings.hint}>Laden…</p>}
      {isError && <p className={settings.error}>Kon de Mollie-status niet laden.</p>}
      {data && !data.connect_available && (
        <p className={settings.hint}>Betalen via een eigen Mollie-account is nog niet beschikbaar. Patiënten betalen voorlopig via Dermalink.</p>
      )}
      {data?.payment_mode === 'platform' && !data.mollie.connected && (
        <p className={settings.hint}>
          Patiënten van {org.name} betalen via het Mollie-account van Dermalink. U hoeft geen eigen Mollie-account te koppelen.
        </p>
      )}
      {data?.connect_available && data.payment_mode === 'connect' && !data.mollie.connected && (
        <>
          <p className={`banner banner-warning ${styles.banner}`}>
            Patiënten kunnen pas een consult starten bij {org.name} zodra het Mollie-account van de praktijk gekoppeld en door Mollie goedgekeurd is.
          </p>
          <p className={settings.hint}>
            Koppel het Mollie-account van {org.name} om de bijdragen van patiënten rechtstreeks op de rekening van de praktijk te ontvangen.
            Heeft de praktijk nog geen Mollie-account, dan maakt u er bij Mollie een aan. Bent u bij Mollie al aangemeld met een ander account,
            meld u daar dan eerst af.
          </p>
          {data.testmode && <p className={`banner banner-info ${styles.banner}`}>Testomgeving: betalingen hier zijn fictief, er wordt geen geld overgemaakt.</p>}
          <div className={settings.actions}>
            <Button variant="primary" onClick={() => { void handleConnect(); }} disabled={busy !== null}>
              {busy === 'connect' ? 'Doorsturen…' : 'Mollie-account koppelen'}
            </Button>
          </div>
        </>
      )}
      {data?.connect_available && data.mollie.connected && (
        <>
          {justLinked && <p className={`banner banner-success ${styles.banner}`}>Mollie-account {data.mollie.account?.name} gekoppeld.</p>}
          {data.testmode && <p className={`banner banner-info ${styles.banner}`}>Testomgeving: betalingen hier zijn fictief, er wordt geen geld overgemaakt.</p>}
          <dl className={styles.statusList}>
            <dt>Gekoppeld account</dt>
            <dd>
              {data.mollie.account ? (
                <>
                  <strong>{data.mollie.account.name}</strong>
                  {data.mollie.account.address_lines.length > 0 && <span className={styles.detail}>{data.mollie.account.address_lines.join(', ')}</span>}
                  {data.mollie.account.registration_number && <span className={styles.detail}>Ondernemingsnummer {data.mollie.account.registration_number}</span>}
                  {data.mollie.account.vat_number && <span className={styles.detail}>Btw {data.mollie.account.vat_number}</span>}
                </>
              ) : 'Onbekend'}
            </dd>
            {data.mollie.connected_at && (
              <>
                <dt>Gekoppeld op</dt>
                <dd>
                  {new Date(data.mollie.connected_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {data.mollie.connected_by && <> door {data.mollie.connected_by}</>}
                </dd>
              </>
            )}
            <dt>Status bij Mollie</dt>
            <dd>
              {mollieLabel(data)}
              <span className={styles.detail}>{mollieExplanation(data)}</span>
            </dd>
            <dt>Betalingen van patiënten</dt>
            <dd>
              {data.payment_mode === 'platform' ? 'Via Dermalink' : data.mollie.can_receive_payments ? 'Naar dit Mollie-account' : 'Nog niet mogelijk'}
              <span className={styles.detail}>
                {data.payment_mode === 'platform'
                  ? 'Patiënten van deze praktijk betalen via het Mollie-account van Dermalink.'
                  : data.mollie.can_receive_payments
                    ? 'Patiënten betalen rechtstreeks aan de praktijk. Dermalink houdt per betaald consult de platformvergoeding in.'
                    : 'Patiënten kunnen een consult starten zodra Mollie het account goedkeurt. Dermalink controleert dit elk uur.'}
              </span>
            </dd>
          </dl>
          <div className={settings.actions}>
            <a className={settings.uploadBtn} href="https://my.mollie.com/dashboard" target="_blank" rel="noopener noreferrer">Mollie-dashboard openen</a>
            {data.payment_mode === 'platform' && (
              <button type="button" className={settings.uploadBtn} onClick={() => { void handleDisconnect(); }} disabled={busy !== null}>
                {busy === 'disconnect' ? 'Ontkoppelen…' : 'Ontkoppelen'}
              </button>
            )}
          </div>
        </>
      )}
      {error && <p className={settings.error}>{error}</p>}
    </div>
  );
}

function mollieLabel({ mollie }: OrgAdminMollieStatusResponse): string {
  if (mollie.can_receive_payments) return 'Klaar voor betalingen';
  if (mollie.onboarding_status === 'in-review') return 'Mollie controleert de gegevens';
  return 'Mollie heeft nog gegevens nodig';
}

function mollieExplanation({ mollie, testmode }: OrgAdminMollieStatusResponse): string {
  if (mollie.onboarding_status === 'completed') return 'Mollie heeft het account goedgekeurd.';
  if (testmode && mollie.can_receive_payments) return 'In de testomgeving kan het account al fictieve betalingen ontvangen. Voor echte betalingen moet Mollie het nog goedkeuren.';
  if (mollie.onboarding_status === 'in-review') return 'U krijgt bericht van Mollie zodra de controle klaar is.';
  return 'Vul de ontbrekende gegevens aan in het Mollie-dashboard.';
}

function FeeCard() {
  const rows = [
    { label: 'Nieuw consult',          price: NEW_CONSULT_PRICE_CENTS,          fee: PLATFORM_FEE_CENTS.new_consult },
    { label: 'Voorschriftenconsult',   price: PRESCRIPTION_CONSULT_PRICE_CENTS, fee: PLATFORM_FEE_CENTS.prescription },
  ];
  return (
    <div className={settings.card}>
      <p className={settings.sectionLabel}>Platformvergoeding</p>
      <p className={settings.hint}>
        De bijdrage van de patiënt is vrijgesteld van btw (medische zorg). Dermalink houdt per betaald consult een vaste platformvergoeding in,
        inclusief 21% btw, en factureert die maandelijks. Mollie rekent daarnaast zijn eigen transactiekosten aan de praktijk aan.
      </p>
      <table className={styles.table}>
        <thead>
          <tr><th>Consult</th><th>Patiënt betaalt</th><th>Platformvergoeding</th><th>waarvan btw</th><th>Voor de praktijk</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td>{euro(r.price)}</td>
              <td>{euro(r.fee)}</td>
              <td>{euro(splitVatInclusive(r.fee).vat_cents)}</td>
              <td>{euro(r.price - r.fee)}</td>
            </tr>
          ))}
          <tr><td>Opvolgconsult</td><td>{euro(0)}</td><td>—</td><td>—</td><td>—</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function InvoicesCard({ org }: { org: OrgAdminOrganization }) {
  const authFetch = useAuthFetch();
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading } = useQuery<OrgAdminInvoiceListResponse>({
    queryKey: ['org-admin-invoices', org.id],
    queryFn:  async () => {
      const res = await authFetch(`/org-admin/organizations/${org.id}/invoices`);
      if (!res.ok) throw new Error('Failed to load invoices');
      return res.json();
    },
  });

  async function handleDownload(invoiceId: number) {
    setError(null);
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/invoices/${invoiceId}/pdf`);
      if (!res.ok) throw new Error();
      const { url } = await res.json() as InvoicePdfUrlResponse;
      window.open(url, '_blank', 'noopener');
    } catch {
      setError('Downloaden mislukt.');
    }
  }

  return (
    <div className={settings.card}>
      <p className={settings.sectionLabel}>Facturen</p>
      {isLoading && <p className={settings.hint}>Laden…</p>}
      {data && data.invoices.length === 0 && <p className={settings.hint}>Nog geen facturen.</p>}
      {data && data.invoices.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr><th>Nummer</th><th>Periode</th><th>Excl. btw</th><th>Btw</th><th>Totaal</th><th /></tr>
          </thead>
          <tbody>
            {data.invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.invoice_number}</td>
                <td>{new Date(inv.period_start).toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })}</td>
                <td>{euro(inv.net_cents)}</td>
                <td>{euro(inv.vat_cents)}</td>
                <td>{euro(inv.total_cents)}</td>
                <td><button type="button" className={styles.linkButton} onClick={() => { void handleDownload(inv.id); }}>PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {error && <p className={settings.error}>{error}</p>}
    </div>
  );
}

/**
 * Mollie redirects here (MOLLIE_CONNECT_REDIRECT_URL) after the admin authorises Dermalink.
 * The org id travels in `state`, so the right practice is completed even when the admin
 * manages several.
 */
export function PracticePaymentsMollieCallback() {
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const navigate    = useNavigate();
  const [params]    = useSearchParams();
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const started = useRef(false);

  const code  = params.get('code');
  const state = params.get('state');
  const orgId = Number(state?.split('.')[0]);
  const valid = Boolean(code && state && Number.isInteger(orgId));
  // Mollie sends ?error=… instead of a code when the admin cancels or something fails on its side.
  const error = valid ? callbackError : (params.get('error_description') ?? 'De koppeling met Mollie werd geannuleerd.');

  useEffect(() => {
    if (!valid || started.current) return;
    started.current = true;
    void (async () => {
      const res = await authFetch(`/org-admin/organizations/${orgId}/mollie/callback`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ code, state }),
      });
      const body = await res.json() as OrgAdminMollieStatusResponse & { message?: string };
      if (!res.ok) {
        setCallbackError(body.message ?? 'Kon de Mollie-koppeling niet voltooien.');
        return;
      }
      queryClient.setQueryData(mollieQueryKey(orgId), body);
      navigate(ROUTES.practiceAdminPayments, { replace: true, state: { mollieLinked: true } });
    })();
  }, [authFetch, code, navigate, orgId, queryClient, state, valid]);

  return (
    <div className={admin.container}>
      <div className={`${admin.inner} ${admin.innerNarrow}`}>
        <div className={settings.card}>
          <p className={settings.sectionLabel}>Mollie-account</p>
          {error
            ? <>
                <p className={settings.error}>{error}</p>
                <div className={settings.actions}>
                  <Button variant="primary" onClick={() => navigate(ROUTES.practiceAdminPayments, { replace: true })}>Terug naar Betalingen</Button>
                </div>
              </>
            : <p className={settings.hint}>Koppeling met Mollie voltooien…</p>}
        </div>
      </div>
    </div>
  );
}
